'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';

// Classic template components
import HeroWithSearch from '@/components/HeroWithSearch';
import TeamSection from '@/components/TeamSection';
import Accolades from '@/components/Accolades';
import ClassicFeaturedProperty from '@/components/ClassicFeaturedProperty';
import FeaturedAspenProperties from '@/components/FeaturedAspenProperties';
import CityStats from '@/components/CityStats';
import InTheNews from '@/components/InTheNews';
import type { FeaturedListing } from '@/components/ClassicFeaturedProperty';
import type { CarouselListing } from '@/components/FeaturedAspenProperties';
import type { CityStatsData } from '@/components/CityStats';

// RC Sotheby's template components — hero eagerly loaded, rest lazy
import RCSothebysHero from '@/components/RCSothebysHero';
const RCSothebysPropertyCarousel = dynamic(() => import('@/components/RCSothebysPropertyCarousel'), {
  ssr: false
});
const RCSothebysAbout = dynamic(() => import('@/components/RCSothebysAbout'), {
  ssr: false
});
const RCSothebysInquirySection = dynamic(() => import('@/components/RCSothebysInquirySection'), {
  ssr: false
});

// Luxury template components
import LuxuryHero from '@/components/LuxuryHero';
const LuxuryPropertyCarousel = dynamic(() => import('@/components/LuxuryPropertyCarousel'), {
  ssr: false
});
const LuxuryAbout = dynamic(() => import('@/components/LuxuryAbout'), {
  ssr: false
});
const LuxuryQuoteBlock = dynamic(() => import('@/components/LuxuryQuoteBlock'), {
  ssr: false
});
const LuxuryCityStats = dynamic(() => import('@/components/LuxuryCityStats'), {
  ssr: false
});
const LuxuryFeaturedProperty = dynamic(() => import('@/components/LuxuryFeaturedProperty'), {
  ssr: false
});
const LuxuryCommunities = dynamic(() => import('@/components/LuxuryCommunities'), {
  ssr: false
});

// Modern template components
import ModernHero from '@/components/ModernHero';
const ModernAbout = dynamic(() => import('@/components/ModernAbout'), {
  ssr: false
});
const ModernQuoteBlock = dynamic(() => import('@/components/ModernQuoteBlock'), {
  ssr: false
});
const ModernFeaturedProperty = dynamic(() => import('@/components/ModernFeaturedProperty'), {
  ssr: false
});
const ModernPropertyCarousel = dynamic(() => import('@/components/ModernPropertyCarousel'), {
  ssr: false
});
const ModernCityStats = dynamic(() => import('@/components/ModernCityStats'), {
  ssr: false
});
const ModernAgentListingsGallery = dynamic(() => import('@/components/ModernAgentListingsGallery'), {
  ssr: false
});
const ModernNewestListings = dynamic(() => import('@/components/ModernNewestListings'), {
  ssr: false
});
const ModernCommunities = dynamic(() => import('@/components/ModernCommunities'), {
  ssr: false
});
const ModernContactCTA = dynamic(() => import('@/components/ModernContactCTA'), {
  ssr: false
});
interface HomepageContentProps {
  // Template selection from Sanity
  template?: 'classic' | 'luxury' | 'modern' | 'custom-one' | 'rcsothebys-custom';
  // Hero data
  videoUrl?: string;
  fallbackImageUrl?: string;
  heroVideos?: Array<{
    videoUrl?: string;
    muxPlaybackId?: string;
    posterUrl?: string;
  }>;
  heroTitle?: string;
  heroSubtitle?: string;
  heroSeoDescription?: string;
  showSearch?: boolean;
  showTitleSubtitle?: boolean;

  // Team section data
  teamSection?: {
    enabled?: boolean;
    title?: string;
    imagePosition?: 'left' | 'right';
    featuredTeamMember?: any;
    primaryButtonText?: string;
    primaryButtonLink?: string;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
  };

  // Accolades data
  accolades?: {
    enabled?: boolean;
    title?: string;
    seoDescription?: string;
    backgroundImage?: any;
    items?: any[];
  };

  // Featured property data
  featuredProperty?: {
    enabled?: boolean;
    mlsId?: string;
    mlsIds?: string[];
    title?: string;
    headline?: string;
    buttonText?: string;
    videos?: string[];
  };

  // Agent MLS ID from team member (for featured property gallery)
  agentMlsId?: string;

  // Office name filter for hero/carousel (only show team listings)
  officeName?: string;

  // Minimum price filter for hero gallery
  heroMinPrice?: number;

  // Sort order for hero gallery ('price' = most expensive first)
  heroSortBy?: 'date' | 'price';

  // Override limit for hero gallery
  heroLimit?: number;

  // Server-fetched properties for immediate hero render (no loading flash)
  heroProperties?: any[];

  // Featured properties carousel data
  featuredPropertiesCarousel?: {
    enabled?: boolean;
    title?: string;
    subtitle?: string;
    seoDescription?: string;
    cities?: string[];
    limit?: number;
    buttonText?: string;
  };

  // Featured Communities data
  featuredCommunities?: {
    title?: string;
    communities?: Array<{
      _id: string;
      title: string;
      slug: {
        current: string;
      };
      description?: string;
      imageUrl?: string;
    }>;
  };

  // Market Stats section data
  marketStatsSection?: {
    enabled?: boolean;
    title?: string;
    subtitle?: string;
    seoDescription?: string;
    cities?: Array<{
      city: string;
      enabled: boolean;
    }>;
  };

  // Branding logo for sections that need it
  logoUrl?: string;
  logoAlt?: string;

  // In the News section data
  inTheNews?: {
    enabled?: boolean;
    title?: string;
    subtitle?: string;
    articles?: Array<{
      _id: string;
      title: string;
      slug?: string;
      sourceName: string;
      sourceLogo?: any;
      url: string;
      image?: any;
      excerpt?: string;
      publishedAt?: string;
    }>;
  };

  // Server-fetched data for the classic sections that otherwise load in the
  // browser (see src/lib/homepageSections.ts), so the homepage HTML carries
  // them for crawlers that do not run JavaScript. Undefined means the section
  // fetches after load as it used to.
  featuredListingsInitial?: FeaturedListing[];
  cityStatsInitial?: {
    cities: string[];
    stats: CityStatsData[];
  };
  newestListingsInitial?: CarouselListing[];
}
export default function HomepageContent(t0) {
  const $ = _c(175);
  const {
    template: t1,
    videoUrl,
    fallbackImageUrl,
    heroVideos,
    heroTitle,
    heroSubtitle,
    heroSeoDescription,
    showSearch,
    showTitleSubtitle,
    teamSection,
    accolades,
    featuredProperty,
    agentMlsId,
    officeName,
    heroMinPrice,
    heroSortBy,
    heroLimit,
    heroProperties,
    featuredPropertiesCarousel,
    featuredCommunities,
    marketStatsSection,
    logoUrl,
    logoAlt,
    featuredListingsInitial,
    cityStatsInitial,
    newestListingsInitial
  } = t0;
  const template = t1 === undefined ? "classic" : t1;
  let t2;
  if ($[0] !== marketStatsSection?.cities) {
    t2 = marketStatsSection?.cities?.filter(_temp)?.map(_temp2) || [];
    $[0] = marketStatsSection?.cities;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const marketStatsCities = t2;
  if (template === "modern" || template === "custom-one") {
    const t3 = heroTitle || "Exceptional Properties";
    const t4 = heroSubtitle || "Discover a curated collection of the world's finest residences";
    let t5;
    if ($[2] !== fallbackImageUrl || $[3] !== t3 || $[4] !== t4 || $[5] !== videoUrl) {
      t5 = <ModernHero videoUrl={videoUrl} fallbackImageUrl={fallbackImageUrl} title={t3} subtitle={t4} />;
      $[2] = fallbackImageUrl;
      $[3] = t3;
      $[4] = t4;
      $[5] = videoUrl;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    const t6 = teamSection?.title || "Uncompromising Excellence";
    const t7 = teamSection?.featuredTeamMember;
    const t8 = teamSection?.primaryButtonText;
    const t9 = teamSection?.primaryButtonLink;
    let t10;
    if ($[7] !== t6 || $[8] !== t7 || $[9] !== t8 || $[10] !== t9) {
      t10 = <ModernAbout title={t6} teamMember={t7} primaryButtonText={t8} primaryButtonLink={t9} />;
      $[7] = t6;
      $[8] = t7;
      $[9] = t8;
      $[10] = t9;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    let t11;
    if ($[12] !== accolades) {
      t11 = accolades?.items && accolades.items.length > 0 && <ModernQuoteBlock title={accolades.title || "The Standard of Excellence"} items={accolades.items} />;
      $[12] = accolades;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== agentMlsId || $[15] !== featuredProperty) {
      t12 = featuredProperty?.enabled && (featuredProperty?.mlsId || agentMlsId) && <ModernFeaturedProperty mlsId={featuredProperty.mlsId} agentMlsId={agentMlsId} headline={featuredProperty.headline} buttonText={featuredProperty.buttonText} />;
      $[14] = agentMlsId;
      $[15] = featuredProperty;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    let t13;
    if ($[17] !== marketStatsCities || $[18] !== marketStatsSection?.enabled || $[19] !== marketStatsSection?.subtitle || $[20] !== marketStatsSection?.title || $[21] !== template) {
      t13 = template === "custom-one" && marketStatsSection?.enabled !== false && <ModernCityStats title={marketStatsSection?.title} subtitle={marketStatsSection?.subtitle} configuredCities={marketStatsCities.length > 0 ? marketStatsCities : undefined} variant="dark" />;
      $[17] = marketStatsCities;
      $[18] = marketStatsSection?.enabled;
      $[19] = marketStatsSection?.subtitle;
      $[20] = marketStatsSection?.title;
      $[21] = template;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== featuredPropertiesCarousel?.cities || $[24] !== featuredPropertiesCarousel?.limit || $[25] !== template) {
      t14 = template === "custom-one" && <ModernNewestListings cities={featuredPropertiesCarousel?.cities || undefined} limit={featuredPropertiesCarousel?.limit || 8} />;
      $[23] = featuredPropertiesCarousel?.cities;
      $[24] = featuredPropertiesCarousel?.limit;
      $[25] = template;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] !== featuredCommunities || $[28] !== template) {
      t15 = template === "custom-one" && featuredCommunities?.communities && featuredCommunities.communities.length > 0 && <ModernCommunities title={featuredCommunities.title} communities={featuredCommunities.communities} />;
      $[27] = featuredCommunities;
      $[28] = template;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== template) {
      t16 = template === "custom-one" && <ModernContactCTA />;
      $[30] = template;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== featuredPropertiesCarousel?.cities || $[33] !== featuredPropertiesCarousel?.enabled || $[34] !== featuredPropertiesCarousel?.limit || $[35] !== featuredPropertiesCarousel?.subtitle || $[36] !== featuredPropertiesCarousel?.title || $[37] !== template) {
      t17 = template !== "custom-one" && featuredPropertiesCarousel?.enabled !== false && <ModernPropertyCarousel cities={featuredPropertiesCarousel?.cities} title={featuredPropertiesCarousel?.title || "Featured Properties"} subtitle={featuredPropertiesCarousel?.subtitle} limit={featuredPropertiesCarousel?.limit || 8} />;
      $[32] = featuredPropertiesCarousel?.cities;
      $[33] = featuredPropertiesCarousel?.enabled;
      $[34] = featuredPropertiesCarousel?.limit;
      $[35] = featuredPropertiesCarousel?.subtitle;
      $[36] = featuredPropertiesCarousel?.title;
      $[37] = template;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    let t18;
    if ($[39] !== marketStatsCities || $[40] !== marketStatsSection?.enabled || $[41] !== marketStatsSection?.subtitle || $[42] !== marketStatsSection?.title || $[43] !== template) {
      t18 = template !== "custom-one" && marketStatsSection?.enabled !== false && <ModernCityStats title={marketStatsSection?.title} subtitle={marketStatsSection?.subtitle} configuredCities={marketStatsCities.length > 0 ? marketStatsCities : undefined} />;
      $[39] = marketStatsCities;
      $[40] = marketStatsSection?.enabled;
      $[41] = marketStatsSection?.subtitle;
      $[42] = marketStatsSection?.title;
      $[43] = template;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t5) {
      t19 = <>{t5}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}</>;
      $[45] = t10;
      $[46] = t11;
      $[47] = t12;
      $[48] = t13;
      $[49] = t14;
      $[50] = t15;
      $[51] = t16;
      $[52] = t17;
      $[53] = t18;
      $[54] = t5;
      $[55] = t19;
    } else {
      t19 = $[55];
    }
    return t19;
  }
  if (template === "rcsothebys-custom") {
    const t3 = featuredPropertiesCarousel?.cities;
    const t4 = heroLimit || featuredPropertiesCarousel?.limit || 8;
    let t5;
    if ($[56] !== fallbackImageUrl || $[57] !== heroMinPrice || $[58] !== heroProperties || $[59] !== heroSortBy || $[60] !== officeName || $[61] !== t3 || $[62] !== t4 || $[63] !== videoUrl) {
      t5 = <RCSothebysHero cities={t3} limit={t4} videoUrl={videoUrl} fallbackImageUrl={fallbackImageUrl} officeName={officeName} minPrice={heroMinPrice} sortBy={heroSortBy} initialProperties={heroProperties} />;
      $[56] = fallbackImageUrl;
      $[57] = heroMinPrice;
      $[58] = heroProperties;
      $[59] = heroSortBy;
      $[60] = officeName;
      $[61] = t3;
      $[62] = t4;
      $[63] = videoUrl;
      $[64] = t5;
    } else {
      t5 = $[64];
    }
    let t6;
    if ($[65] !== teamSection) {
      t6 = teamSection?.enabled !== false && teamSection?.featuredTeamMember && <RCSothebysAbout title={teamSection.title} teamMember={teamSection.featuredTeamMember} primaryButtonText={teamSection.primaryButtonText} primaryButtonLink={teamSection.primaryButtonLink} secondaryButtonText={teamSection.secondaryButtonText} secondaryButtonLink={teamSection.secondaryButtonLink} />;
      $[65] = teamSection;
      $[66] = t6;
    } else {
      t6 = $[66];
    }
    let t7;
    if ($[67] !== featuredProperty) {
      t7 = featuredProperty?.enabled && (featuredProperty?.mlsId || featuredProperty?.mlsIds && featuredProperty.mlsIds.length > 0) && <ClassicFeaturedProperty mlsId={featuredProperty.mlsId} mlsIds={featuredProperty.mlsIds} headline={featuredProperty.headline} buttonText={featuredProperty.buttonText} videos={featuredProperty.videos} />;
      $[67] = featuredProperty;
      $[68] = t7;
    } else {
      t7 = $[68];
    }
    let t8;
    if ($[69] !== featuredPropertiesCarousel?.buttonText || $[70] !== featuredPropertiesCarousel?.cities || $[71] !== featuredPropertiesCarousel?.enabled || $[72] !== featuredPropertiesCarousel?.limit || $[73] !== featuredPropertiesCarousel?.subtitle || $[74] !== featuredPropertiesCarousel?.title || $[75] !== heroLimit || $[76] !== heroMinPrice || $[77] !== heroProperties || $[78] !== heroSortBy || $[79] !== officeName) {
      t8 = featuredPropertiesCarousel?.enabled !== false && <RCSothebysPropertyCarousel cities={featuredPropertiesCarousel?.cities} title={featuredPropertiesCarousel?.title || "Featured Listings"} subtitle={featuredPropertiesCarousel?.subtitle} limit={heroLimit || featuredPropertiesCarousel?.limit || 8} buttonText={featuredPropertiesCarousel?.buttonText || "View All Properties"} officeName={officeName} minPrice={heroMinPrice} sortBy={heroSortBy} initialProperties={heroProperties} />;
      $[69] = featuredPropertiesCarousel?.buttonText;
      $[70] = featuredPropertiesCarousel?.cities;
      $[71] = featuredPropertiesCarousel?.enabled;
      $[72] = featuredPropertiesCarousel?.limit;
      $[73] = featuredPropertiesCarousel?.subtitle;
      $[74] = featuredPropertiesCarousel?.title;
      $[75] = heroLimit;
      $[76] = heroMinPrice;
      $[77] = heroProperties;
      $[78] = heroSortBy;
      $[79] = officeName;
      $[80] = t8;
    } else {
      t8 = $[80];
    }
    let t9;
    if ($[81] !== marketStatsCities || $[82] !== marketStatsSection?.enabled || $[83] !== marketStatsSection?.seoDescription || $[84] !== marketStatsSection?.subtitle || $[85] !== marketStatsSection?.title) {
      t9 = marketStatsSection?.enabled !== false && <CityStats title={marketStatsSection?.title} subtitle={marketStatsSection?.subtitle} seoDescription={marketStatsSection?.seoDescription} configuredCities={marketStatsCities.length > 0 ? marketStatsCities : undefined} />;
      $[81] = marketStatsCities;
      $[82] = marketStatsSection?.enabled;
      $[83] = marketStatsSection?.seoDescription;
      $[84] = marketStatsSection?.subtitle;
      $[85] = marketStatsSection?.title;
      $[86] = t9;
    } else {
      t9 = $[86];
    }
    let t10;
    if ($[87] !== logoAlt || $[88] !== logoUrl) {
      t10 = <RCSothebysInquirySection logoUrl={logoUrl} logoAlt={logoAlt} />;
      $[87] = logoAlt;
      $[88] = logoUrl;
      $[89] = t10;
    } else {
      t10 = $[89];
    }
    let t11;
    if ($[90] !== accolades) {
      t11 = accolades?.enabled !== false && accolades?.items && accolades.items.length > 0 && <Accolades title={accolades.title} seoDescription={accolades.seoDescription} backgroundImage={accolades.backgroundImage} items={accolades.items} />;
      $[90] = accolades;
      $[91] = t11;
    } else {
      t11 = $[91];
    }
    let t12;
    if ($[92] !== t10 || $[93] !== t11 || $[94] !== t5 || $[95] !== t6 || $[96] !== t7 || $[97] !== t8 || $[98] !== t9) {
      t12 = <>{t5}{t6}{t7}{t8}{t9}{t10}{t11}</>;
      $[92] = t10;
      $[93] = t11;
      $[94] = t5;
      $[95] = t6;
      $[96] = t7;
      $[97] = t8;
      $[98] = t9;
      $[99] = t12;
    } else {
      t12 = $[99];
    }
    return t12;
  }
  if (template === "classic") {
    const t3 = showSearch !== false;
    const t4 = showTitleSubtitle !== false;
    let t5;
    if ($[100] !== fallbackImageUrl || $[101] !== heroSeoDescription || $[102] !== heroSubtitle || $[103] !== heroTitle || $[104] !== heroVideos || $[105] !== t3 || $[106] !== t4 || $[107] !== videoUrl) {
      t5 = <HeroWithSearch videoUrl={videoUrl} fallbackImageUrl={fallbackImageUrl} heroVideos={heroVideos} title={heroTitle} subtitle={heroSubtitle} seoDescription={heroSeoDescription} showSearch={t3} showTitleSubtitle={t4} />;
      $[100] = fallbackImageUrl;
      $[101] = heroSeoDescription;
      $[102] = heroSubtitle;
      $[103] = heroTitle;
      $[104] = heroVideos;
      $[105] = t3;
      $[106] = t4;
      $[107] = videoUrl;
      $[108] = t5;
    } else {
      t5 = $[108];
    }
    let t6;
    if ($[109] !== teamSection) {
      t6 = teamSection?.enabled !== false && teamSection?.featuredTeamMember && <TeamSection title={teamSection.title} imagePosition={teamSection.imagePosition} teamMember={teamSection.featuredTeamMember} primaryButtonText={teamSection.primaryButtonText} primaryButtonLink={teamSection.primaryButtonLink} secondaryButtonText={teamSection.secondaryButtonText} secondaryButtonLink={teamSection.secondaryButtonLink} />;
      $[109] = teamSection;
      $[110] = t6;
    } else {
      t6 = $[110];
    }
    let t7;
    if ($[111] !== featuredListingsInitial || $[112] !== featuredProperty) {
      t7 = featuredProperty?.enabled && (featuredProperty?.mlsId || featuredProperty?.mlsIds && featuredProperty.mlsIds.length > 0) && <ClassicFeaturedProperty mlsId={featuredProperty.mlsId} mlsIds={featuredProperty.mlsIds} headline={featuredProperty.headline} buttonText={featuredProperty.buttonText} videos={featuredProperty.videos} initialProperties={featuredListingsInitial} />;
      $[111] = featuredListingsInitial;
      $[112] = featuredProperty;
      $[113] = t7;
    } else {
      t7 = $[113];
    }
    let t8;
    if ($[114] !== cityStatsInitial || $[115] !== marketStatsCities || $[116] !== marketStatsSection?.enabled || $[117] !== marketStatsSection?.seoDescription || $[118] !== marketStatsSection?.subtitle || $[119] !== marketStatsSection?.title) {
      t8 = marketStatsSection?.enabled !== false && <CityStats title={marketStatsSection?.title} subtitle={marketStatsSection?.subtitle} seoDescription={marketStatsSection?.seoDescription} configuredCities={marketStatsCities.length > 0 ? marketStatsCities : undefined} initialData={cityStatsInitial} />;
      $[114] = cityStatsInitial;
      $[115] = marketStatsCities;
      $[116] = marketStatsSection?.enabled;
      $[117] = marketStatsSection?.seoDescription;
      $[118] = marketStatsSection?.subtitle;
      $[119] = marketStatsSection?.title;
      $[120] = t8;
    } else {
      t8 = $[120];
    }
    let t9;
    if ($[121] !== accolades) {
      t9 = accolades?.enabled !== false && accolades?.items && accolades.items.length > 0 && <Accolades title={accolades.title} seoDescription={accolades.seoDescription} backgroundImage={accolades.backgroundImage} items={accolades.items} />;
      $[121] = accolades;
      $[122] = t9;
    } else {
      t9 = $[122];
    }
    let t10;
    if ($[123] !== featuredPropertiesCarousel?.buttonText || $[124] !== featuredPropertiesCarousel?.cities || $[125] !== featuredPropertiesCarousel?.enabled || $[126] !== featuredPropertiesCarousel?.limit || $[127] !== featuredPropertiesCarousel?.seoDescription || $[128] !== featuredPropertiesCarousel?.subtitle || $[129] !== featuredPropertiesCarousel?.title || $[130] !== newestListingsInitial) {
      t10 = featuredPropertiesCarousel?.enabled !== false && <FeaturedAspenProperties cities={featuredPropertiesCarousel?.cities} title={featuredPropertiesCarousel?.title} subtitle={featuredPropertiesCarousel?.subtitle} seoDescription={featuredPropertiesCarousel?.seoDescription} limit={featuredPropertiesCarousel?.limit} buttonText={featuredPropertiesCarousel?.buttonText} initialProperties={newestListingsInitial} />;
      $[123] = featuredPropertiesCarousel?.buttonText;
      $[124] = featuredPropertiesCarousel?.cities;
      $[125] = featuredPropertiesCarousel?.enabled;
      $[126] = featuredPropertiesCarousel?.limit;
      $[127] = featuredPropertiesCarousel?.seoDescription;
      $[128] = featuredPropertiesCarousel?.subtitle;
      $[129] = featuredPropertiesCarousel?.title;
      $[130] = newestListingsInitial;
      $[131] = t10;
    } else {
      t10 = $[131];
    }
    let t11;
    if ($[132] !== t10 || $[133] !== t5 || $[134] !== t6 || $[135] !== t7 || $[136] !== t8 || $[137] !== t9) {
      t11 = <>{t5}{t6}{t7}{t8}{t9}{t10}</>;
      $[132] = t10;
      $[133] = t5;
      $[134] = t6;
      $[135] = t7;
      $[136] = t8;
      $[137] = t9;
      $[138] = t11;
    } else {
      t11 = $[138];
    }
    return t11;
  }
  const t3 = heroTitle || "Extraordinary Residences";
  const t4 = heroSubtitle || "Discover an unparalleled collection of luxury properties";
  let t5;
  if ($[139] !== fallbackImageUrl || $[140] !== t3 || $[141] !== t4 || $[142] !== videoUrl) {
    t5 = <LuxuryHero videoUrl={videoUrl} fallbackImageUrl={fallbackImageUrl} title={t3} subtitle={t4} />;
    $[139] = fallbackImageUrl;
    $[140] = t3;
    $[141] = t4;
    $[142] = videoUrl;
    $[143] = t5;
  } else {
    t5 = $[143];
  }
  const t6 = teamSection?.title || "The Art of Exceptional Service";
  const t7 = teamSection?.featuredTeamMember;
  const t8 = teamSection?.primaryButtonText;
  const t9 = teamSection?.primaryButtonLink;
  let t10;
  if ($[144] !== t6 || $[145] !== t7 || $[146] !== t8 || $[147] !== t9) {
    t10 = <LuxuryAbout title={t6} teamMember={t7} primaryButtonText={t8} primaryButtonLink={t9} />;
    $[144] = t6;
    $[145] = t7;
    $[146] = t8;
    $[147] = t9;
    $[148] = t10;
  } else {
    t10 = $[148];
  }
  let t11;
  if ($[149] !== accolades) {
    t11 = accolades?.items && accolades.items.length > 0 && <LuxuryQuoteBlock title={accolades.title || "The Distinction"} items={accolades.items} />;
    $[149] = accolades;
    $[150] = t11;
  } else {
    t11 = $[150];
  }
  let t12;
  if ($[151] !== agentMlsId || $[152] !== featuredProperty) {
    t12 = featuredProperty?.enabled && (featuredProperty?.mlsId || agentMlsId) && <LuxuryFeaturedProperty mlsId={featuredProperty.mlsId} agentMlsId={agentMlsId} title={featuredProperty.title} headline={featuredProperty.headline} buttonText={featuredProperty.buttonText} />;
    $[151] = agentMlsId;
    $[152] = featuredProperty;
    $[153] = t12;
  } else {
    t12 = $[153];
  }
  let t13;
  if ($[154] !== featuredPropertiesCarousel?.cities || $[155] !== featuredPropertiesCarousel?.enabled || $[156] !== featuredPropertiesCarousel?.limit || $[157] !== featuredPropertiesCarousel?.subtitle || $[158] !== featuredPropertiesCarousel?.title) {
    t13 = featuredPropertiesCarousel?.enabled !== false && <LuxuryPropertyCarousel cities={featuredPropertiesCarousel?.cities} title={featuredPropertiesCarousel?.title || "Curated Residences"} subtitle={featuredPropertiesCarousel?.subtitle} limit={featuredPropertiesCarousel?.limit || 8} />;
    $[154] = featuredPropertiesCarousel?.cities;
    $[155] = featuredPropertiesCarousel?.enabled;
    $[156] = featuredPropertiesCarousel?.limit;
    $[157] = featuredPropertiesCarousel?.subtitle;
    $[158] = featuredPropertiesCarousel?.title;
    $[159] = t13;
  } else {
    t13 = $[159];
  }
  let t14;
  if ($[160] !== marketStatsCities || $[161] !== marketStatsSection?.enabled || $[162] !== marketStatsSection?.subtitle || $[163] !== marketStatsSection?.title) {
    t14 = marketStatsSection?.enabled !== false && <LuxuryCityStats title={marketStatsSection?.title} subtitle={marketStatsSection?.subtitle} configuredCities={marketStatsCities.length > 0 ? marketStatsCities : undefined} />;
    $[160] = marketStatsCities;
    $[161] = marketStatsSection?.enabled;
    $[162] = marketStatsSection?.subtitle;
    $[163] = marketStatsSection?.title;
    $[164] = t14;
  } else {
    t14 = $[164];
  }
  let t15;
  if ($[165] !== featuredCommunities) {
    t15 = featuredCommunities?.communities && featuredCommunities.communities.length > 0 && <LuxuryCommunities title={featuredCommunities.title} communities={featuredCommunities.communities} />;
    $[165] = featuredCommunities;
    $[166] = t15;
  } else {
    t15 = $[166];
  }
  let t16;
  if ($[167] !== t10 || $[168] !== t11 || $[169] !== t12 || $[170] !== t13 || $[171] !== t14 || $[172] !== t15 || $[173] !== t5) {
    t16 = <>{t5}{t10}{t11}{t12}{t13}{t14}{t15}</>;
    $[167] = t10;
    $[168] = t11;
    $[169] = t12;
    $[170] = t13;
    $[171] = t14;
    $[172] = t15;
    $[173] = t5;
    $[174] = t16;
  } else {
    t16 = $[174];
  }
  return t16;
}
function _temp2(c_0) {
  return c_0.city;
}
function _temp(c) {
  return c.enabled;
}